<template>
    <div class="container">
        <v-title :breadcrumb="breadcrumb">{{pageTitle}}</v-title>
        <div class="grid">
            <div class="grid-item grid-item-primary">
                <div class="grid">
                    <div class="grid-item grid-item-full">
                        <div class="tile position-relative">
                            <v-inline-loader v-if="isLoading"/>

                            <div class="tile-content" v-if="integration.hasConfigurations">
                                <ul class="list list-group list-divided">
                                    <li class="list-item align-items-center" v-for="(configuration, index) in integration.configurations" :key="`${configuration.eventType}-${index}`">
                                        <div class="avatar avatar-l no-flex inline-m">
                                            <div class="avatar-image" v-fs-block>
                                                <figure class="image image-avatar">
                                                    <img :alt="integration.displayLabel" :src="integration.logoSrc"/>
                                                </figure>
                                            </div>
                                        </div>
                                        <div>
                                            <h2>{{configuration.eventType | unKebabCase}}</h2>
                                            <p>{{configuration.title}}</p>
                                        </div>
                                        <v-popper2 class="no-flex" position="bottomEnd">
                                            <template #trigger="{popper}">
                                                <v-icon-button
                                                    class="no-padding"
                                                    size="small"
                                                    type="basic"
                                                    @click="popper.toggle">
                                                    <v-icon icon="actions"/>
                                                </v-icon-button>
                                            </template>
                                            <template #content>
                                                <div class="popover">
                                                    <div class="popover-control">
                                                        <div class="popover-content popover-menu">
                                                            <a
                                                                class="popover-menu-item"
                                                                v-push="{name: 'integration-edit', params: {service: integration.service, id: configuration.id}}">
                                                                Edit
                                                            </a>
                                                            <a
                                                                class="popover-menu-item popover-menu-item-negative"
                                                                @click="openRemoveDeleteModal(configuration)">
                                                                Remove Connection
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </v-popper2>
                                    </li>
                                </ul>
                            </div>

                            <v-no-content :img-src="integration.logoSrc" v-else>
                                <span slot="title">It seems that you have no {{integration.displayLabel}} integrations</span>
                                <router-link
                                    v-if="integration.oauth2ConnectUrl"
                                    slot="actions"
                                    class="btn btn-primary" :to="{name: 'integration-add', params: {service: integration.service}}">
                                    Add {{integration.displayLabel}} Integration
                                </router-link>
                            </v-no-content>
                        </div>

                        <div class="margin-top-l align-right">
                            <router-link class="btn inline-s" :to="{name: 'integrations'}">
                                Close
                            </router-link>
                            <router-link
                                v-if="integration.oauth2ConnectUrl"
                                class="btn btn-primary" :to="{name: 'integration-add', params: {service: integration.service}}">
                                Add New
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-secondary">
                <integrations-read-more :integration="integration" />
            </div>
        </div>

        <!--Delete Integration Modal -->
        <v-modal v-if="selectedConfigurationToRemove" :title="`Delete ${integration.displayLabel} Integration`" @close="closeModal" size="small">
            <div slot="content">
                <p>Are you sure you want to delete this {{integration.displayLabel}} integration?</p>
                <p class="margin-top-m"><strong>{{selectedConfigurationToRemove.eventType | unKebabCase}}</strong></p>
                <p>{{selectedConfigurationToRemove.title}}</p>
            </div>
            <template slot="actions">
                <r-button type="danger" @click="removeIntegration">Delete {{integration.displayLabel}} Integration</r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import vModal from '@/components/modal/v-modal';
    import vTitle from '@/components/v-title';
    import {
        vIconButton,
        vIcon,
    } from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import vNoContent from '@/components/v-no-content';
    import vPopper2 from '@/components/popper/v-popper2';
    import IntegrationsReadMore from '@/modules/integrations/components/integrations-read-more';
    import IntegrationModel from '@/common/models/integration-model';
    import RuleSetModel from '@/common/models/rules/rule-set-model';

    export default {
        components: {
            vModal,
            vTitle,
            vIconButton,
            vIcon,
            vInlineLoader,
            vNoContent,
            vPopper2,
            IntegrationsReadMore,
        },
        data() {
            return {
                isLoading: false,
                integration: new IntegrationModel({service: this.$route.params.service}),
                selectedConfigurationToRemove: null,
            };
        },
        computed: {
            pageTitle() {
                return `App - ${this.integration.displayLabel}`;
            },
            breadcrumb() {
                return {
                    label: this.pageTitle,
                    route: {
                        name: 'integration-view',
                        params: {service: this.integration.service},
                    },
                };
            },
        },
        methods: {
            async fetchData() {
                try {
                    const params = {
                        service: this.integration.service,
                    };
                    const {fields} = await this.$api(({integrations}) => integrations.get(params));
                    this.integration = new IntegrationModel(fields);
                } catch (error) {
                    this.$toast.fromError(error);
                }
            },
            openRemoveDeleteModal(configuration) {
                this.selectedConfigurationToRemove = configuration;
            },
            closeModal() {
                this.selectedConfigurationToRemove = null;
            },
            async removeIntegration() {
                this.isLoading = true;
                const configuration = this.selectedConfigurationToRemove;
                const {service} = this.$route.params;
                this.selectedConfigurationToRemove = null;
                try {
                    const {eventType} = configuration;
                    const {fields} = await this.$api(({events}) => events.getRules({eventType}));
                    const ruleSet = new RuleSetModel(fields);
                    ruleSet.removeIntegration(service);
                    await this.$api(({events}) => events.updateRules({eventType, data: ruleSet.getPayload()}));
                    await this.fetchData();
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isLoading = false;
                }
            },
        },
        async mounted() {
            this.isLoading = true;
            await this.fetchData();
            this.isLoading = false;
        },
    };
</script>
